import { EventEmitter } from 'events';
import { Socket } from 'socket.io-client';
import { Colors, Config } from '~/constants';
import {
  EditHeroDispatcher,
  HeroBuildDispatcher,
  PlayerDispatcher,
} from '~/flux/dispatchers';
import text from '~/text';
import { awaitSocket, registerDispatchHandlers } from '~/Tools';
import mutationUpgradeAbility from '~/graphql/hero/mutationUpgradeAbility';
import mutationDowngradeAbility from '~/graphql/hero/mutationDowngradeAbility';
import mutationUpgradeAttribute from '~/graphql/hero/mutationUpgradeAttribute';
import mutationDowngradeAttribute from '~/graphql/hero/mutationDowngradeAttribute';
import mutationEquipAbility from '~/graphql/hero/mutationEquipAbility';
import mutationUnequipAbility from '~/graphql/hero/mutationUnequipAbility';
import mutationResetAbilities from '~/graphql/hero/mutationResetAbilities';
import mutationResetAttributes from '~/graphql/hero/mutationResetAttributes';
import { findHeroBuildById } from '~/helpers/findHeroBuildById';
import { logError } from '~/logger';
import * as handlers from './handlers';
import messages from './messages';

let playerId: string | null = null;
let _socket: Socket;

// the stuff we serve:
let hero_builds: any;
let pending_ability_upgrade_handle: string | boolean = false;

const HeroBuildStore = Object.assign({}, EventEmitter.prototype, {
  ...messages,

  getAll() {
    return {
      hero_builds,
      pending_ability_upgrade_handle,
    };
  },
});
// Initialize EventEmitter internal state
EventEmitter.call(HeroBuildStore);
export default HeroBuildStore;

const emit = HeroBuildStore.emit.bind(HeroBuildStore);

function setPlayerId(val: string) {
  playerId = val;
}

function setHeroBuilds(builds: any) {
  hero_builds = builds;
}

EditHeroDispatcher.register(
  registerDispatchHandlers({
    [EditHeroDispatcher.RESET_ABILITIES_CONFIRM]: requestAbilitiesReset,
    [EditHeroDispatcher.UPGRADE_ABILITY]: requestAbilityUpgrade,
    [EditHeroDispatcher.DOWNGRADE_ABILITY]: requestAbilityDowngrade,
    [EditHeroDispatcher.EQUIP_ABILITY]: requestEquipAbility,
    [EditHeroDispatcher.UNEQUIP_ABILITY]: requestUnequipAbility,
    [EditHeroDispatcher.RESET_ATTRIBUTES_CONFIRM]: requestAttributeReset,
    [EditHeroDispatcher.UPGRADE_ATTRIBUTE]: requestAttributeUpgrade,
    [EditHeroDispatcher.DOWNGRADE_ATTRIBUTE]: requestAttributeDowngrade,
    [EditHeroDispatcher.MOVE_AI_ROLE_TO_INDEX]: requestMoveAIRoleToIndex,
    [EditHeroDispatcher.INCREMENT_AFFINITY]: requestIncrementHeroAffinity,
  })
);
PlayerDispatcher.register(
  registerDispatchHandlers({
    [PlayerDispatcher.PLAYER_LOGGED_IN]: onPlayerLoggedIn,
  })
);
HeroBuildDispatcher.register(
  registerDispatchHandlers({
    [HeroBuildDispatcher.CREATE_NEW_HERO_BUILD]: requestCreateNewHeroBuild,
    [HeroBuildDispatcher.COPY_HERO_BUILD]: (action: {
      hero_handle: string;
      hero_build_id: string;
    }) => {
      return handlers.copyHeroBuild(
        {
          playerId,
          heroBuilds: hero_builds,
          setHeroBuilds,
          emit,
        },
        action
      );
    },
    [HeroBuildDispatcher.DELETE_HERO_BUILD]: requestDeleteHeroBuild,
    [HeroBuildDispatcher.ENGAGE_HERO_BULID]: requestEngageHeroBuild,
    [HeroBuildDispatcher.RENAME_HERO_BULID]: requestRenameHeroBuild,
  })
);

awaitSocket(onSocketConnected);
function onSocketConnected(socket) {
  _socket = socket;
  if (!_socket.has_HeroBuildStore_listeners) {
    _socket.on('ability_change', onAbilityChange);
    _socket.on('ai_priorities_change', onAIPrioritiesChange);
    _socket.on('attribute_change', onAttributeChange);
    _socket.on('hero_build_deleted', onHeroBuildDeleted);
    _socket.on('hero_build_renamed', onHeroBuildRenamed);
    _socket.on('hero_builds', onHeroBuilds);
    _socket.on('heroUnlocked', onHeroUnlocked);
    _socket.on('invalid_hero_build_name', onInvalidHeroBuildName);
    _socket.on('item_equipped', onItemEquipped);
    _socket.on('item_unequipped', onItemUnequipped);
    _socket.on('new_hero_build_created', onNewHeroBuildCreated);

    _socket.has_HeroBuildStore_listeners = true;
  }
}

function onPlayerLoggedIn(action: { player: any }) {
  try {
    const { player } = action;
    setPlayerId(action.player._id);
    hero_builds = player.gameState.hero_builds;
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onPlayerLoggedIn',
      action,
    });
  }
}

function onHeroBuilds(data: { hero_builds: any }) {
  try {
    hero_builds = data.hero_builds;
    HeroBuildStore.emit(messages.GOT_HERO_BUILDS, hero_builds);
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onHeroBuilds',
      data,
    });
  }
}

function onHeroUnlocked(data: { hero_builds: any; heroHandle: string }) {
  try {
    hero_builds = data.hero_builds;
    HeroBuildStore.emit(messages.HERO_UNLOCKED, data.heroHandle);
    HeroBuildStore.emit(messages.GOT_HERO_BUILDS, hero_builds);
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onHeroUnlocked',
      data,
    });
  }
}

function requestCreateNewHeroBuild(action: { hero_handle: string; new_build_name: string }) {
  try {
    const { hero_handle, new_build_name } = action;

    // validation is done in the API, but lets try to avoid sending overly long strings
    if (
      !new_build_name ||
      new_build_name.length < 1 ||
      new_build_name.length > 20
    ) {
      $addMessageLogMessage(
        text('server.error.hero_build_name_rule_2'),
        Colors.RED
      );
      return;
    }

    _socket.emit('create_new_hero_build', {
      playerId,
      hero_handle,
      new_build_name,
      locale: Config.LOCALE,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestCreateNewHeroBuild',
      action,
    });
  }
}

function onInvalidHeroBuildName(data: { message: string }) {
  $addMessageLogMessage(
    `${text('ui.invalid_username')} ${data.message}`,
    Colors.RED
  );
}

function onNewHeroBuildCreated(data: { hero_builds: any }) {
  try {
    hero_builds = data.hero_builds;
    HeroBuildStore.emit(messages.GOT_HERO_BUILDS, hero_builds);
    HeroBuildStore.emit(messages.NEW_HERO_BUILD_CREATED);
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onNewHeroBuildCreated',
      data,
    });
  }
}

function requestDeleteHeroBuild(action: { hero_handle: string; build_id: string }) {
  try {
    const { hero_handle, build_id } = action;
    _socket.emit('delete_hero_build', {
      playerId,
      hero_handle,
      build_id,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestDeleteHeroBuild',
      action,
    });
  }
}

function onHeroBuildDeleted(data: { hero_builds: any }) {
  try {
    hero_builds = data.hero_builds;
    HeroBuildStore.emit(messages.GOT_HERO_BUILDS, hero_builds);
    HeroBuildStore.emit(messages.HERO_BUILD_DELETED);
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onHeroBuildDeleted',
      data,
    });
  }
}

function onItemEquipped(data: { hero_handle: string; this_hero_builds: any }) {
  try {
    hero_builds[data.hero_handle] = data.this_hero_builds;
    HeroBuildStore.emit(messages.ITEM_EQUIPPED);
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onItemEquipped',
      data,
    });
  }
}

function onItemUnequipped(data: { hero_handle: string; this_hero_builds: any }) {
  try {
    hero_builds[data.hero_handle] = data.this_hero_builds;
    HeroBuildStore.emit(messages.ITEM_UNEQUIPPED);
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onItemUnequipped',
      data,
    });
  }
}

async function requestAbilityUpgrade({
  hero_handle,
  ability_handle,
  hero_build_id,
}: {
  hero_handle: string;
  ability_handle: string;
  hero_build_id: string;
}) {
  try {
    pending_ability_upgrade_handle = ability_handle;

    const result = await mutationUpgradeAbility({
      playerId: playerId!,
      heroHandle: hero_handle,
      abilityHandle: ability_handle,
      heroBuildId: hero_build_id,
    });

    // Trigger the ability change event with the result
    onAbilityChange({
      hero_handle: result.heroHandle,
      this_hero_build: result.heroBuild,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestAbilityUpgrade',
    });
    pending_ability_upgrade_handle = false;
  }
}

async function requestAbilityDowngrade({
  hero_handle,
  ability_handle,
  hero_build_id,
}: {
  hero_handle: string;
  ability_handle: string;
  hero_build_id: string;
}) {
  try {
    pending_ability_upgrade_handle = ability_handle;

    const result = await mutationDowngradeAbility({
      playerId: playerId!,
      heroHandle: hero_handle,
      abilityHandle: ability_handle,
      heroBuildId: hero_build_id,
    });

    // Trigger the ability change event with the result
    onAbilityChange({
      hero_handle: result.heroHandle,
      this_hero_build: result.heroBuild,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestAbilityDowngrade',
    });
    pending_ability_upgrade_handle = false;
  }
}

function onAbilityChange(data: { hero_handle: string; this_hero_build: any }) {
  try {
    const { hero_handle, this_hero_build } = data;

    const buildId = this_hero_build.hero_build_id || this_hero_build._id;
    const build_index = hero_builds[hero_handle].findIndex(
      (build: any) => build.hero_build_id === buildId || build._id === buildId
    );
    hero_builds[hero_handle][build_index] = this_hero_build;

    pending_ability_upgrade_handle = false;
    HeroBuildStore.emit(messages.ABILITY_CHANGE);

    // Dispatch action so other stores (like TutorialStore) can react
    HeroBuildDispatcher.handleHeroBuildAction({
      actionType: HeroBuildDispatcher.ABILITY_CHANGED,
      hero_handle,
      this_hero_build,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onAbilityChange',
      data,
    });
  }
}

async function requestAbilitiesReset({
  hero_build_id,
  hero_handle,
}: {
  hero_build_id: string;
  hero_handle: string;
}) {
  try {
    HeroBuildStore.emit(messages.RESET_ABILITIES_REQUESTED);

    const result = await mutationResetAbilities({
      playerId: playerId!,
      heroHandle: hero_handle,
      heroBuildId: hero_build_id,
    });

    // Trigger the ability change event with the result
    onAbilityChange({
      hero_handle: result.heroHandle,
      this_hero_build: result.heroBuild,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestAbilitiesReset',
    });
  }
}

async function requestEquipAbility({
  ability_handle,
  hero_handle,
  hero_build_id,
}: {
  ability_handle: string;
  hero_handle: string;
  hero_build_id: string;
}) {
  try {
    const result = await mutationEquipAbility({
      playerId: playerId!,
      heroHandle: hero_handle,
      abilityHandle: ability_handle,
      heroBuildId: hero_build_id,
    });

    // Trigger the ability change event with the result
    onAbilityChange({
      hero_handle: result.heroHandle,
      this_hero_build: result.heroBuild,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestEquipAbility',
    });
  }
}

async function requestUnequipAbility({
  ability_handle,
  hero_handle,
  hero_build_id,
}: {
  ability_handle: string;
  hero_handle: string;
  hero_build_id: string;
}) {
  try {
    const result = await mutationUnequipAbility({
      playerId: playerId!,
      heroHandle: hero_handle,
      abilityHandle: ability_handle,
      heroBuildId: hero_build_id,
    });

    // Trigger the ability change event with the result
    onAbilityChange({
      hero_handle: result.heroHandle,
      this_hero_build: result.heroBuild,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestUnequipAbility',
    });
  }
}

function onAttributeChange(data: { hero_handle: string; this_hero_build: any }) {
  try {
    const { hero_handle, this_hero_build } = data;

    const buildId = this_hero_build.hero_build_id || this_hero_build._id;
    const build_index = hero_builds[hero_handle].findIndex(
      (build: any) => build.hero_build_id === buildId || build._id === buildId
    );
    hero_builds[hero_handle][build_index] = this_hero_build;
    HeroBuildStore.emit(messages.ATTRIBUTE_CHANGE);
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onAttributeChange',
      data,
    });
  }
}

async function requestAttributeUpgrade({
  hero_handle,
  attribute_handle,
  hero_build_id,
}: {
  hero_handle: string;
  attribute_handle: string;
  hero_build_id: string;
}) {
  try {
    const result = await mutationUpgradeAttribute({
      playerId: playerId!,
      heroHandle: hero_handle,
      attributeHandle: attribute_handle,
      heroBuildId: hero_build_id,
    });

    // Trigger the attribute change event with the result
    onAttributeChange({
      hero_handle: result.heroHandle,
      this_hero_build: result.heroBuild,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestAttributeUpgrade',
    });
  }
}

async function requestAttributeDowngrade({
  hero_handle,
  attribute_handle,
  hero_build_id,
}: {
  hero_handle: string;
  attribute_handle: string;
  hero_build_id: string;
}) {
  try {
    const result = await mutationDowngradeAttribute({
      playerId: playerId!,
      heroHandle: hero_handle,
      attributeHandle: attribute_handle,
      heroBuildId: hero_build_id,
    });

    // Trigger the attribute change event with the result
    onAttributeChange({
      hero_handle: result.heroHandle,
      this_hero_build: result.heroBuild,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestAttributeDowngrade',
    });
  }
}

async function requestAttributeReset({
  hero_handle,
  hero_build_id,
}: {
  hero_handle: string;
  hero_build_id: string;
}) {
  try {
    const result = await mutationResetAttributes({
      playerId: playerId!,
      heroHandle: hero_handle,
      heroBuildId: hero_build_id,
    });

    // Trigger the attribute change event with the result
    onAttributeChange({
      hero_handle: result.heroHandle,
      this_hero_build: result.heroBuild,
    });
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'requestAttributeReset',
    });
  }
}

function requestMoveAIRoleToIndex({
  ai_tree_tag,
  hero_build_id,
  hero_handle,
  target_index,
}: {
  ai_tree_tag: string;
  hero_build_id: string;
  hero_handle: string;
  target_index: number;
}) {
  _socket.emit('move_ai_role_to_index', {
    playerId,
    ai_tree_tag,
    hero_build_id,
    hero_handle,
    target_index,
  });
}

function onAIPrioritiesChange(data: { hero_handle: string; this_hero_build: any }) {
  try {
    const { hero_handle, this_hero_build } = data;

    const build_index = hero_builds[hero_handle].findIndex(
      ({ _id }: any) => _id === this_hero_build._id
    );
    hero_builds[hero_handle][build_index] = this_hero_build;
    HeroBuildStore.emit(messages.AI_PRIORITIES_CHANGE);
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'onAIPrioritiesChange',
      data,
    });
  }
}

function requestEngageHeroBuild(action: any) {
  _socket.emit('engage_hero_build', {
    playerId,
    action,
  });
}

function requestRenameHeroBuild(action: any) {
  _socket.emit('rename_hero_build', {
    playerId,
    ...action,
    locale: Config.LOCALE,
  });
}

function onHeroBuildRenamed(data: { updated_hero_builds: any }) {
  hero_builds = {
    ...hero_builds,
    ...data.updated_hero_builds,
  };
  HeroBuildStore.emit(messages.GOT_HERO_BUILDS, hero_builds);
}

function requestIncrementHeroAffinity({
  hero_handle,
  hero_build_id,
  affinity_type,
  unit_type_handle,
  value,
}: {
  hero_handle: string;
  hero_build_id: string;
  affinity_type: string;
  unit_type_handle: string;
  value: number;
}) {
  _socket.emit('increment_hero_affinity', {
    playerId,
    hero_handle,
    hero_build_id,
    affinity_type,
    unit_type_handle,
    value,
  });
}
